import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext.jsx';

/**
 * Google Login Button Component
 * Handles Google Identity Services authentication
 */
const GoogleLoginButton = ({
  className = '',
  size = 'lg',
  disabled = false,
  onSuccess,
  onError
}) => {
  const buttonContainerRef = useRef(null);
  const { loginWithGoogle, loading: authLoading } = useAuth();
  const [isInitialized, setIsInitialized] = useState(false);
  const [sdkLoaded, setSdkLoaded] = useState(false);
  const initAttemptedRef = useRef(false);

  // Check if Google SDK is loaded
  useEffect(() => {

    
    const checkGoogleSDK = () => {
      const googleExists = !!window.google;
      const accountsExist = !!window.google?.accounts;
      const idExists = !!window.google?.accounts?.id;
      
      
      
      if (window.google?.accounts?.id) {
        setSdkLoaded(true);
        return true;
      }
      return false;
    };

    if (checkGoogleSDK()) return;

    const pollInterval = setInterval(() => {
      
      if (checkGoogleSDK()) {
        clearInterval(pollInterval);
        console.log('✅ SDK found after polling');
      }
    }, 100);

    const timeout = setTimeout(() => {
      clearInterval(pollInterval);
      if (!window.google?.accounts?.id) {
        console.error('❌ Google Identity Services SDK failed to load');
        console.error('❌ Available window properties:', {
          google: typeof window.google,
          accounts: window.google?.accounts ? 'exists' : 'missing',
          id: window.google?.accounts?.id ? 'exists' : 'missing'
        });
        if (onError) {
          onError('Google authentication service is unavailable. Please refresh the page.');
        }
      }
    }, 10000);

    return () => {
      clearInterval(pollInterval);
      clearTimeout(timeout);
    };
  }, [onError]);

  // Initialize and render Google button
  useEffect(() => {
    if (!sdkLoaded || isInitialized || !buttonContainerRef.current || initAttemptedRef.current) {
      return;
    }

    initAttemptedRef.current = true;

    const initializeGoogleButton = async () => {
      try {
       
        const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
        
        if (!clientId) {
          console.error('❌ Google Client ID not configured');
          
          if (onError) {
            onError('Google authentication is not properly configured. Please contact support.');
          }
          return;
        }

  

        const handleCredentialResponse = async (response) => {

          
          if (!response.credential) {
            console.error('❌ No credential received from Google');
            if (onError) {
              onError('Google sign-in failed. Please try again.');
            }
            return;
          }

          try {

            
            const { default: authService } = await import('../../services/authService.js');
            const result = await authService.authenticateWithGoogle(response.credential);

            
            if (onSuccess) {
              onSuccess(result);
            }
          } catch (error) {
            console.error('❌ Authentication error:', error);
            if (onError) {
              onError(error.message || 'Authentication failed. Please try again.');
            }
          }
        };

        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: handleCredentialResponse,
          auto_select: false,
          cancel_on_tap_outside: true,
          context: 'signin',
          ux_mode: 'popup',
          use_fedcm_for_prompt: false
        });



        if (buttonContainerRef.current) {
          // Clear container
          buttonContainerRef.current.innerHTML = '';
          
          const buttonConfig = {
            theme: 'outline',
            size: size === 'sm' ? 'small' : size === 'md' ? 'medium' : 'large',
            text: 'signin_with',
            shape: 'pill',
            width: size === 'sm' ? 200 : size === 'md' ? 250 : 320,
            logo_alignment: 'left'
          };


          
          window.google.accounts.id.renderButton(
            buttonContainerRef.current,
            buttonConfig
          );


          setIsInitialized(true);
          
          // Debug after render
          setTimeout(() => {
            if (buttonContainerRef.current) {
              const iframe = buttonContainerRef.current.querySelector('iframe');

              
              
              if (iframe) {
                
              } else {
                console.error('❌ No iframe found in container!');
                console.error('❌ This usually indicates CSS conflicts preventing iframe creation');
                console.error('❌ Container children:', buttonContainerRef.current.children);
                
                // Apply CSS fixes to resolve iframe creation issues
                //console.log('🔧 Applying CSS fixes for Google button iframe...');
                const container = buttonContainerRef.current;
                
                // Reset potentially conflicting CSS properties
                container.style.transform = 'none';
                container.style.filter = 'none';
                container.style.backdropFilter = 'none';
                container.style.webkitBackdropFilter = 'none';
                container.style.opacity = '1';
                container.style.visibility = 'visible';
                container.style.display = 'block';
                container.style.position = 'relative';
                container.style.zIndex = 'auto';
                
                // Force a reflow
                container.offsetHeight;
                
                // Try to re-render the button
                try {
                  window.google.accounts.id.renderButton(
                    container,
                    { theme: 'outline', size: 'large', text: 'signin_with', shape: 'pill', width: 320 }
                  );
                  //console.log('🔄 Retrying button render after CSS fixes...');
                } catch (renderError) {
                  console.error('❌ Failed to re-render button:', renderError);
                }
              }
            }
          }, 200);
        }
      } catch (error) {
        console.error('❌ Failed to initialize Google button:', error);
        if (onError) {
          onError('Failed to initialize Google sign-in. Please refresh the page.');
        }
      }
    };

    initializeGoogleButton();

    return () => {
      if (buttonContainerRef.current) {
        buttonContainerRef.current.innerHTML = '';
      }
    };
  }, [sdkLoaded, isInitialized, size, onSuccess, onError]);

  // Width based on size
  const containerWidth = {
    sm: '200px',
    md: '250px',
    lg: '320px',
    xl: '360px'
  }[size];

  return (
    <div 
      className={className}
      style={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}
    >
      <div
        style={{
          position: 'relative',
          width: containerWidth,
          minHeight: '48px',
          display: 'block'
        }}
      >
        {/* Pure container for Google button with isolated styles to prevent CSS conflicts */}
        <div
          ref={buttonContainerRef}
          style={{
            width: '100%',
            minHeight: '48px',
            display: 'block',
            position: 'relative',
            zIndex: 9999,
            overflow: 'visible',
            // Critical: Reset all potentially conflicting CSS properties
            transform: 'none',
            filter: 'none',
            backdropFilter: 'none',
            // Fixed: Use correct casing for webkit prefix
            WebkitBackdropFilter: 'none',
            opacity: '1',
            visibility: 'visible',
            background: 'transparent',
            border: 'none',
            borderRadius: '0',
            boxShadow: 'none',
            // Ensure proper isolation from parent CSS
            isolation: 'isolate',
            contain: 'strict',
            // Debug background
            backgroundColor: isInitialized ? 'transparent' : '#f0f0f0'
          }}
        />
        
        {/* Loading overlay */}
        {(authLoading || (!isInitialized && sdkLoaded)) && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '4px',
            zIndex: 20
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{
                width: '20px',
                height: '20px',
                border: '2px solid #ccc',
                borderTopColor: '#666',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
              <span style={{ fontSize: '14px', color: '#666' }}>Loading...</span>
            </div>
          </div>
        )}
        
        {/* Loading SDK message */}
        {!sdkLoaded && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'white',
            border: '1px solid #ddd',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 20
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#666' }}>
              <div style={{
                width: '16px',
                height: '16px',
                border: '2px solid #ccc',
                borderTopColor: '#666',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
              <span>Loading Google...</span>
            </div>
          </div>
        )}
        
        {/* Error state */}
        {sdkLoaded && !isInitialized && initAttemptedRef.current && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: '#fee',
            border: '1px solid #fcc',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 16px',
            zIndex: 20
          }}>
            <span style={{ fontSize: '12px', color: '#c33', textAlign: 'center' }}>
              Failed to load Google sign-in
            </span>
          </div>
        )}
      </div>
      
      {/* Add keyframes for spin animation */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default GoogleLoginButton;